import { useState } from 'react';

import PropTypes from 'prop-types';

import { useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Checkbox from '@zenbusiness/zui/Checkbox';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import Text from '@zenbusiness/zui/Text';

import {
  StyledSummaryCard,
  StyledSummaryRow,
  StyledSummaryDivider,
  StyledContinueButtonRow
} from '../SubscriptionCancelPage.styles';
import { SubscriptionSummaryContent } from './SubscriptionSummaryContent';

export function SubscriptionCancelConfirmation({
  subscription,
  changeReasonDisplay,
  onChangeReason,
  onSubmit
}) {
  const { formatMessage } = useIntl();
  const [termsAgreed, setTermsAgreed] = useState(false);

  return (
    <>
      <Box display="flex" flexDirection="column" gap={1.5}>
        <Text size="2xl" weight="bold" family="secondary">
          {formatMessage({ id: 'dashboard.subscriptionCancel.confirmation.heading' })}
        </Text>
        <Text size="md" color="text.secondary">
          {formatMessage({ id: 'dashboard.subscriptionCancel.confirmation.subheading' })}
        </Text>
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        <StyledSummaryCard>
          <SubscriptionSummaryContent
            period={subscription.period}
            summary={subscription.summary}
            service={subscription.service}
            titleMessageId={subscription.titleMessageId}
            price={subscription.price}
            termStartDate={subscription.termStartDate}
            inTrial={subscription.inTrial}
            trialDays={subscription.trialDays}
            variant="summary"
          />

          <StyledSummaryDivider />

          <StyledSummaryRow>
            <Box>
              <Text size="xs" color="text.secondary">
                {formatMessage({ id: 'dashboard.subscriptionCancel.confirmation.reason.label' })}
              </Text>
              <Text size="sm" weight="bold">{changeReasonDisplay}</Text>
            </Box>
            <Box flex={1} textAlign="right">
              <Button
                variant="text"
                size="small"
                onClick={onChangeReason}
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  minWidth: 'auto',
                  padding: 0
                }}
              >
                {formatMessage({ id: 'dashboard.subscriptionCancel.confirmation.reason.change' })}
              </Button>
            </Box>
          </StyledSummaryRow>
        </StyledSummaryCard>

        <Box>
          <FormControlLabel
            control={(
              <Checkbox
                checked={termsAgreed}
                onChange={(e) => setTermsAgreed(e.target.checked)}
              />
            )}
            label={formatMessage({ id: 'dashboard.subscriptionCancel.confirmation.terms.checkboxLabel' })}
          />
          <Box pl={5}>
            <Text size="xs" color="text.tertiary">
              {formatMessage({ id: 'dashboard.subscriptionCancel.confirmation.terms.disclaimer' })}
            </Text>
          </Box>
        </Box>
      </Box>

      <StyledContinueButtonRow>
        <Button
          variant="contained"
          size="large"
          disabled={!termsAgreed}
          onClick={onSubmit}
          sx={{
            borderRadius: '12px',
            height: 56,
            ...(termsAgreed && {
              backgroundColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.dark'
              }
            })
          }}
        >
          {formatMessage({ id: 'dashboard.subscriptionCancel.confirmation.submit' })}
        </Button>
      </StyledContinueButtonRow>
    </>
  );
}

SubscriptionCancelConfirmation.propTypes = {
  subscription: PropTypes.shape({
    service: PropTypes.string,
    summary: PropTypes.string,
    titleMessageId: PropTypes.string,
    price: PropTypes.number,
    period: PropTypes.string,
    inTrial: PropTypes.bool,
    trialDays: PropTypes.number,
    termStartDate: PropTypes.string
  }).isRequired,
  changeReasonDisplay: PropTypes.string.isRequired,
  onChangeReason: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};
